// Current school (data-model §5; FR-025; contracts/session-seam.md §5). Set by later features from
// the selected student (004/005); default null. Changing it re-renders the header without a reload.
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useSyncExternalStore,
} from "react";

import type { SchoolIdentity } from "@/session/types";

export type CurrentSchoolStore = {
  get(): SchoolIdentity | null;
  set(school: SchoolIdentity | null): void;
  subscribe(listener: () => void): () => void;
};

export function createCurrentSchoolStore(
  initial: SchoolIdentity | null = null,
): CurrentSchoolStore {
  let current = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => current,
    set(school) {
      if (school === current) return;
      current = school;
      listeners.forEach((l) => l());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The app's single current-school store (module level). */
export const appCurrentSchool = createCurrentSchoolStore();

/** Personal-data cleaner: run on sign-out (constitution VI). */
export function clearCurrentSchool(): void {
  appCurrentSchool.set(null);
}

const CurrentSchoolContext =
  createContext<CurrentSchoolStore>(appCurrentSchool);

export function CurrentSchoolProvider({
  children,
  store,
}: {
  children: ReactNode;
  store?: CurrentSchoolStore;
}) {
  return (
    <CurrentSchoolContext.Provider value={store ?? appCurrentSchool}>
      {children}
    </CurrentSchoolContext.Provider>
  );
}

export function useCurrentSchool(): [
  SchoolIdentity | null,
  (school: SchoolIdentity | null) => void,
] {
  const store = useContext(CurrentSchoolContext);
  const school = useSyncExternalStore(store.subscribe, store.get, store.get);
  const setSchool = useCallback(
    (s: SchoolIdentity | null) => store.set(s),
    [store],
  );
  return [school, setSchool];
}
